import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

export default function SportsCampPage() {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);

  useEffect(() => {
    // Update document title and meta tags
    document.title = "Sports Camp Photography for All Ages - Case Study | Esteban's Stache";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Comprehensive documentation of multi-sport youth camp with diverse participants. Modern energetic imagery with custom baseball cards for youth and nostalgic portraits for adults.');
    }
    
    // Update canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', '/projects/sports-camp');
    }

    // Scroll to top when page loads
    window.scrollTo(0, 0);
  }, []);

  const project = {
    title: 'Sports Camp Photography for All Ages',
    category: 'Event Photography',
    description: 'Comprehensive documentation of a multi-sport youth camp with diverse participants across different age groups.',
    image: 'https://static.readdy.ai/image/3fdc746f0d1d84f2cde4df046cd392e8/01a2c8c01c4130c6ae24726c2e3d0e14.jpeg',
    challenge: 'Traditional sports photography often feels impersonal and outdated. Youth leagues get quick snapshots with little artistry, while adult recreational athletes rarely have access to photography that reflects their passion for the game. We set out to change that.',
    solution: 'We created two tailored photography experiences designed around what each group values most. For youth athletes, we capture modern, energetic imagery that highlights action, teamwork, and excitement — paired with bold, dynamic designs and collectible custom baseball cards. For adult and senior leagues, we offer nostalgic, high-quality portraits and action photos inspired by classic sports memorabilia. At the heart of the program are our custom baseball cards — a perfect blend of photography, design, and storytelling. For adults, they\'re a nod to the past; for kids, a badge of pride; and for everyone, a lasting reminder of why we play.',
    results: [
      '25,000+ Iconic photos in the most iconic stadium',
      'Repeat customers',
      'High participation in Youth sector'
    ],
    gallery: [
      'https://static.readdy.ai/image/3fdc746f0d1d84f2cde4df046cd392e8/011028373b9f890146371a6f4b837cde.jpeg',
      'https://static.readdy.ai/image/3fdc746f0d1d84f2cde4df046cd392e8/d0204169be4bdddbb02d9bdf822631bf.jpeg'
    ]
  };

  const galleryImages = [
    {
      src: 'https://static.readdy.ai/image/3fdc746f0d1d84f2cde4df046cd392e8/011028373b9f890146371a6f4b837cde.jpeg',
      alt: 'Sports Camp Training Session',
      caption: 'Adult baseball cards blend modern fun with vintage nostalgia, echoing the classic designs fans grew up loving.'
    },
    {
      src: 'https://static.readdy.ai/image/3fdc746f0d1d84f2cde4df046cd392e8/c4786a2f7df518ebc73b5bdad8df6cad.jpeg',
      alt: 'Sports Camp Team Activities',
      caption: 'I designed a customizable on-site print package that combines individual and group photos, delivered quickly and tailored to each camper. Official Dodger assets help streamline the process and provide cohesive branding throughout.'
    },
    {
      src: 'https://static.readdy.ai/image/3fdc746f0d1d84f2cde4df046cd392e8/d0204169be4bdddbb02d9bdf822631bf.jpeg',
      alt: 'Youth Baseball Cards with Dodger Assets',
      caption: 'Youth cards use contemporary designs and incorporate Dodger assets'
    }
  ];

  const handlePrevImage = () => {
    if (selectedImage !== null) {
      setSelectedImage(selectedImage === 0 ? galleryImages.length - 1 : selectedImage - 1);
    }
  };

  const handleNextImage = () => {
    if (selectedImage !== null) {
      setSelectedImage(selectedImage === galleryImages.length - 1 ? 0 : selectedImage + 1);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (selectedImage === null) return;
    if (e.key === 'Escape') setSelectedImage(null);
    if (e.key === 'ArrowLeft') handlePrevImage();
    if (e.key === 'ArrowRight') handleNextImage();
  };

  useEffect(() => {
    if (selectedImage !== null) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [selectedImage]);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-50 to-blue-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-6">
            <Link 
              to="/work" 
              className="flex items-center text-teal-600 hover:text-teal-700 font-medium transition-colors duration-200"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              Back to Portfolio
            </Link>
          </div>
          <div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{project.title}</h1>
            <p className="text-xl text-teal-600 font-medium">{project.category}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Hero Image */}
        <div className="relative h-96 md:h-[500px] rounded-2xl overflow-hidden mb-16">
          <img
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover object-top"
          />
        </div>

        {/* Project Overview */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Project Overview</h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            {project.description}
          </p>
        </div>

        {/* Project Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-16">
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-6">The Challenge</h3>
            <p className="text-gray-600 leading-relaxed mb-8">
              {project.challenge}
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-6">Our Solution</h3>
            <p className="text-gray-600 leading-relaxed">
              {project.solution}
            </p>
          </div>

          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Results Achieved</h3>
            <div className="space-y-4">
              {project.results.map((result, index) => (
                <div key={index} className="flex items-start">
                  <div className="w-6 h-6 flex items-center justify-center bg-teal-100 rounded-full mr-4 mt-0.5">
                    <i className="ri-check-line text-teal-600 text-sm"></i>
                  </div>
                  <span className="text-gray-700 font-medium">{result}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Gallery */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-8">Project Gallery</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleryImages.map((image, index) => (
              <div 
                key={index} 
                className="aspect-square rounded-xl overflow-hidden group cursor-pointer"
                onClick={() => setSelectedImage(index)}
              >
                <img 
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
                  <i className="ri-search-line text-white text-4xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></i>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage !== null && (
        <div 
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-6"
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-6 right-6 text-white hover:text-gray-300 transition-colors z-10 w-12 h-12 flex items-center justify-center"
            aria-label="Close lightbox"
          >
            <i className="ri-close-line text-4xl"></i>
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              handlePrevImage();
            }}
            className="absolute left-6 text-white hover:text-gray-300 transition-colors z-10 w-12 h-12 flex items-center justify-center"
            aria-label="Previous image"
          >
            <i className="ri-arrow-left-s-line text-5xl"></i>
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              handleNextImage();
            }}
            className="absolute right-6 text-white hover:text-gray-300 transition-colors z-10 w-12 h-12 flex items-center justify-center"
            aria-label="Next image"
          >
            <i className="ri-arrow-right-s-line text-5xl"></i>
          </button>

          <div 
            className="w-full h-full flex items-center justify-center px-24"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={galleryImages[selectedImage].src}
              alt={galleryImages[selectedImage].alt}
              className="max-w-full max-h-[calc(100vh-200px)] object-contain"
            />
            <div className="absolute bottom-8 left-0 right-0 text-center">
              <p className="text-white text-lg mb-2">
                {galleryImages[selectedImage].caption}
              </p>
              <p className="text-white text-sm opacity-75">
                {selectedImage + 1} / {galleryImages.length}
              </p>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
